package com.swetdot.swetgram.data.tasks

import com.swetdot.swetgram.cache.SwetgramCache
import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.ioDispatcher
import com.swetdot.swetgram.data.newUuid
import com.swetdot.swetgram.domain.DailyTaskDestination
import com.swetdot.swetgram.domain.DailyTaskItem
import com.swetdot.swetgram.domain.DailyTaskKind
import com.swetdot.swetgram.domain.DailyTaskState
import com.swetdot.swetgram.domain.DailyTasks
import com.swetdot.swetgram.domain.TasksProfile
import com.swetdot.swetgram.protocol.ProtocolJson
import com.swetdot.swetgram.protocol.dto.DailyTask
import com.swetdot.swetgram.protocol.dto.DailyTaskAction
import com.swetdot.swetgram.protocol.dto.DailyTaskSet
import com.swetdot.swetgram.protocol.dto.DailyTaskStatus
import com.swetdot.swetgram.protocol.dto.DailyTaskType
import com.swetdot.swetgram.protocol.dto.ReplaceDailyTaskRequest
import com.swetdot.swetgram.protocol.dto.ReplaceUnavailableStepTaskRequest
import com.swetdot.swetgram.protocol.dto.ReportTaskStepsRequest
import com.swetdot.swetgram.protocol.dto.StepTaskUnavailableReason
import com.swetdot.swetgram.protocol.dto.TaskStepSource
import com.swetdot.swetgram.protocol.dto.TaskProfile
import com.swetdot.swetgram.protocol.dto.UpdateTaskPreferencesRequest
import io.ktor.client.request.setBody
import kotlinx.coroutines.withContext
import kotlin.time.Clock

class TasksRepository(
    private val api: ApiClient,
    private val cache: SwetgramCache,
) {
    suspend fun cachedToday(): DailyTasks? = withContext(ioDispatcher) {
        val row = cache.cacheQueries.selectCachedDailyTasks(TODAY_CACHE_KEY).executeAsOneOrNull()
            ?: return@withContext null
        runCatching { ProtocolJson.decodeFromString<DailyTaskSet>(row.tasksJson).toDomain() }
            .getOrElse {
                cache.cacheQueries.clearAllDailyTasks()
                null
            }
    }

    suspend fun today(timezone: String): DailyTasks {
        val current = profile()
        if (current.timezone != timezone) updatePreferences(current, timezone)
        val dto = api.get<DailyTaskSet>("users/me/tasks/today")
        cache(dto)
        return dto.toDomain()
    }

    suspend fun profile(): TasksProfile = api.get<TaskProfile>("users/me/tasks/profile").toDomain()

    suspend fun replace(task: DailyTaskItem, setVersion: Int): DailyTasks {
        val clientId = newUuid()
        val dto = api.post<DailyTaskSet>(
            path = "users/me/tasks/today/replace",
            idempotencyKey = clientId,
        ) {
            setBody(ReplaceDailyTaskRequest(clientId, task.id, setVersion))
        }
        cache(dto)
        return dto.toDomain()
    }

    suspend fun reportSteps(total: DailyStepsTotal): DailyTasks {
        val clientId = newUuid()
        val dto = api.post<DailyTaskSet>(
            path = "users/me/tasks/steps-progress",
            idempotencyKey = clientId,
        ) {
            setBody(
                ReportTaskStepsRequest(
                    clientId = clientId,
                    localDate = total.localDate,
                    steps = total.steps.coerceIn(0, MAX_DAILY_STEPS),
                    source = when (total.provider) {
                        DailyStepsProvider.HEALTH_CONNECT -> TaskStepSource.HEALTH_CONNECT
                        DailyStepsProvider.STEP_COUNTER -> TaskStepSource.STEP_COUNTER
                    },
                    measuredAt = total.measuredAt,
                ),
            )
        }
        cache(dto)
        return dto.toDomain()
    }

    suspend fun replaceUnavailableStep(
        task: DailyTaskItem,
        setVersion: Int,
        reason: UnavailableStepReason,
    ): DailyTasks {
        val clientId = newUuid()
        val dto = api.post<DailyTaskSet>(
            path = "users/me/tasks/today/step-unavailable",
            idempotencyKey = clientId,
        ) {
            setBody(
                ReplaceUnavailableStepTaskRequest(
                    clientId = clientId,
                    taskId = task.id,
                    reason = when (reason) {
                        UnavailableStepReason.PERMISSION_DENIED -> StepTaskUnavailableReason.PERMISSION_DENIED
                        UnavailableStepReason.PERMISSION_REVOKED -> StepTaskUnavailableReason.PERMISSION_REVOKED
                        UnavailableStepReason.PROVIDER_UNAVAILABLE -> StepTaskUnavailableReason.PROVIDER_UNAVAILABLE
                    },
                    version = setVersion,
                ),
            )
        }
        cache(dto)
        return dto.toDomain()
    }

    suspend fun updatePreferences(
        profile: TasksProfile,
        timezone: String,
        stepTasksEnabled: Boolean = profile.stepTasksEnabled,
        notificationsEnabled: Boolean = profile.notificationsEnabled,
    ): TasksProfile = api.patch<TaskProfile>("users/me/tasks/preferences") {
        setBody(
            UpdateTaskPreferencesRequest(
                timezone = timezone,
                stepTasksEnabled = stepTasksEnabled,
                notificationsEnabled = notificationsEnabled,
                version = profile.version,
            ),
        )
    }.toDomain()

    private suspend fun cache(dto: DailyTaskSet) = withContext(ioDispatcher) {
        cache.cacheQueries.upsertCachedDailyTasks(
            cacheKey = TODAY_CACHE_KEY,
            tasksJson = ProtocolJson.encodeToString(DailyTaskSet.serializer(), dto),
            fetchedAt = Clock.System.now().toEpochMilliseconds(),
        )
    }

    private companion object {
        const val TODAY_CACHE_KEY = "today"
        const val MAX_DAILY_STEPS = 200_000
    }
}

private fun DailyTaskSet.toDomain() = DailyTasks(
    id, localDate, timezone, startsAt, expiresAt, tasks.map(DailyTask::toDomain), completedCount,
    totalCount, earnedXp, bonusXp, replacementAvailable, completedAt, version,
)

private fun DailyTask.toDomain() = DailyTaskItem(
    id = id,
    code = code,
    kind = when (type) {
        DailyTaskType.MESSAGE -> DailyTaskKind.MESSAGE
        DailyTaskType.DISCOVER_POST -> DailyTaskKind.DISCOVER_POST
        DailyTaskType.DISCOVER_LIKE -> DailyTaskKind.DISCOVER_LIKE
        DailyTaskType.STEPS -> DailyTaskKind.STEPS
        DailyTaskType.DISCOVER_SAVE -> DailyTaskKind.DISCOVER_SAVE
        DailyTaskType.REPLY -> DailyTaskKind.REPLY
        DailyTaskType.PICSO_POST -> DailyTaskKind.PICSO_POST
        DailyTaskType.PICSO_REACT -> DailyTaskKind.PICSO_REACT
        DailyTaskType.PICSO_SAVE -> DailyTaskKind.PICSO_SAVE
        DailyTaskType.UNKNOWN -> DailyTaskKind.UNKNOWN
    },
    destination = when (action) {
        DailyTaskAction.CHATS -> DailyTaskDestination.CHATS
        DailyTaskAction.DISCOVER -> DailyTaskDestination.DISCOVER
        DailyTaskAction.PICSO -> DailyTaskDestination.PICSO
        DailyTaskAction.STEP_PERMISSION -> DailyTaskDestination.STEP_PERMISSION
        DailyTaskAction.NONE -> DailyTaskDestination.NONE
        DailyTaskAction.UNKNOWN -> DailyTaskDestination.UNKNOWN
    },
    goal = goal,
    progress = progress,
    xpReward = xpReward,
    state = when (status) {
        DailyTaskStatus.ACTIVE -> DailyTaskState.ACTIVE
        DailyTaskStatus.COMPLETED -> DailyTaskState.COMPLETED
        DailyTaskStatus.REPLACED -> DailyTaskState.REPLACED
        DailyTaskStatus.EXPIRED -> DailyTaskState.EXPIRED
        DailyTaskStatus.CANCELLED -> DailyTaskState.CANCELLED
        DailyTaskStatus.UNKNOWN -> DailyTaskState.UNKNOWN
    },
    completedAt = completedAt,
    version = version,
)

private fun TaskProfile.toDomain() = TasksProfile(
    timezone = timezone,
    totalXp = totalXp,
    spendableXp = spendableXp,
    level = level,
    currentStreak = currentStreak,
    longestStreak = longestStreak,
    streakFreezes = streakFreezes,
    stepTasksEnabled = stepTasksEnabled,
    notificationsEnabled = notificationsEnabled,
    version = version,
)
